import React, {
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState,
  useCallback,
  forwardRef,
} from "react";

import { IconButton, Badge, Menu } from "@mui/material";
import _ from "lodash";
import { FaEllipsisV } from "react-icons/fa";

import useStyles, { MoreOptionsWrapper, CurrentOptionsWrapper } from "./styles";

interface MoreOptionComponentProps {
  options?: string[];
  title: string;
  manyOptionsToShow: number;
  singleOptionPerLine?: boolean;

  data?: any[];
  returnLabel?: (item: any) => string;
  asComponent?: (label: string, item: any) => React.ReactChild;
  visibleComponent?: () => React.ReactChild;
}

export interface MoreOptionsComponentHandles {
  anchorEl: HTMLElement | null;
  setAnchor: (anchor: HTMLElement | null) => void;
}

const MoreOptionsComponent: ForwardRefRenderFunction<
  MoreOptionsComponentHandles,
  MoreOptionComponentProps
> = (props, ref) => {
  const { options, title, manyOptionsToShow, singleOptionPerLine = true, ...rest } = props;
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const setAnchor = useCallback((element: HTMLElement | null) => setAnchorEl(element), []);

  useImperativeHandle(ref, () => ({
    anchorEl,
    setAnchor,
  }));

  const renderMenu = (menuChildren: React.ReactNode, notShowOptions?: boolean) => {
    return (
      <div className={classes.wrapper}>
        {notShowOptions ? null : (
          <span>
            {manyOptionsToShow === 1 ? options?.[0] : `${options?.[0]} - ${options?.[1]}`}
          </span>
        )}
        <div>
          <IconButton onClick={(event) => setAnchor(event.currentTarget)}>
            <Badge
              className={classes.optionsBadge}
              badgeContent={
                rest.data
                  ? Number(rest.data?.length) - manyOptionsToShow
                  : Number(options?.length) - manyOptionsToShow
              }
            >
              <div className={classes.optionsButton}>
                <FaEllipsisV size={12} />
              </div>
            </Badge>
          </IconButton>
          <Menu
            className={classes.moreOrdersList}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchor(null)}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            {menuChildren}
          </Menu>
        </div>
      </div>
    );
  };

  const renderComponent = () => {
    if (options?.length === 1) return <span>{options?.join("")}</span>;
    if (Number(options?.length) > manyOptionsToShow)
      return renderMenu(
        <div>
          <header>
            <label>{title}:</label>
          </header>
          <div>
            <span className={classes.singleOptionPerLine}>
              {options?.slice(manyOptionsToShow).join(singleOptionPerLine ? "\n" : " - ")}
            </span>
          </div>
        </div>
      );

    return <span>{options?.join(" - ")}</span>;
  };

  const renderDataComponent = () => {
    const { data, returnLabel, asComponent, visibleComponent } = rest;

    if (!data || !data.length) return null;

    if (data.length === 1 && manyOptionsToShow > 1)
      return <>{asComponent?.(returnLabel?.(data[0]) ?? "", data[0])}</>;

    const dataClone = _.cloneDeep(data);
    const restOptions = manyOptionsToShow <= 0 ? [] : dataClone?.splice(0, manyOptionsToShow);

    const renderMenuChildren = () => {
      return (
        <div>
          <header>
            <label>{title}</label>
          </header>
          <>
            {dataClone?.map((item) => (
              <React.Fragment key={returnLabel?.(item) ?? "option"}>
                {asComponent?.(returnLabel?.(item) ?? "", item)}
              </React.Fragment>
            ))}
          </>
        </div>
      );
    };

    const shouldRenderMenu = (): boolean => {
      return Number(data?.length) > manyOptionsToShow;
    };

    return (
      <MoreOptionsWrapper>
        <CurrentOptionsWrapper>
          {visibleComponent?.() ??
            restOptions?.map((item) => (
              <React.Fragment key={returnLabel?.(item) ?? "option"}>
                {asComponent?.(returnLabel?.(item) ?? "", item)}
              </React.Fragment>
            ))}
        </CurrentOptionsWrapper>
        {shouldRenderMenu() && renderMenu(renderMenuChildren(), true)}
      </MoreOptionsWrapper>
    );
  };

  return options?.length ? <>{renderComponent()}</> : renderDataComponent();
};

export const MoreOptions = forwardRef(MoreOptionsComponent);
